import { z } from 'zod';
import { currencySchema } from './organization.js';
import {
  optionalDate,
  optionalNumber,
  optionalText,
  paginationQuerySchema,
  toPatchSchema,
  uuidSchema,
} from './common.js';

/**
 * Item Lists: named price lists, usually one per year ("2026 Price List").
 *
 * Each entry carries its own price, so one service can cost different amounts
 * in different lists. A quote or invoice copies an entry's price onto the line
 * when it is picked and never reads it back through the list, so editing or
 * archiving a list rewrites no document.
 */

export const ITEM_LIST_STATUSES = ['ACTIVE', 'ARCHIVED'] as const;
export type ItemListStatus = (typeof ITEM_LIST_STATUSES)[number];

const money = z.coerce.number().min(0).max(1e15);
const rate = z.coerce.number().min(0).max(100);

export const createItemListSchema = z.object({
  name: z.string().trim().min(1, 'Required').max(160),
  /** Any year the business plans for; nothing here knows which exist. */
  // A blank input submits '': blank means no year, not 0 and not an error.
  year: optionalNumber(z.coerce.number().int().min(1990).max(2200)),
  effectiveFrom: optionalDate(),
  effectiveTo: optionalDate(),
  currency: currencySchema.default('INR'),
  notes: optionalText(4000),
});
export type CreateItemListInput = z.infer<typeof createItemListSchema>;

export const updateItemListSchema = toPatchSchema(createItemListSchema).extend({
  status: z.enum(ITEM_LIST_STATUSES).optional(),
});
export type UpdateItemListInput = z.infer<typeof updateItemListSchema>;

/** Copying a list: the new name is required, the year and price change optional. */
export const duplicateItemListSchema = z.object({
  name: z.string().trim().min(1, 'Required').max(160),
  year: optionalNumber(z.coerce.number().int().min(1990).max(2200)),
  /** Raises (or lowers, if negative) every copied price by this percentage. */
  // Blank means keep every price.
  adjustPercent: z
    .union([z.literal(''), z.coerce.number().min(-100).max(1000)])
    .transform((value) => (value === '' ? 0 : value))
    .default(0),
});
export type DuplicateItemListInput = z.infer<typeof duplicateItemListSchema>;

export const ITEM_LIST_SORT_FIELDS = ['name', 'year', 'createdAt', 'updatedAt'] as const;
export const listItemListsQuerySchema = paginationQuerySchema.extend({
  sortBy: z.enum(ITEM_LIST_SORT_FIELDS).default('year'),
  status: z.enum(ITEM_LIST_STATUSES).optional(),
  year: z.coerce.number().int().optional(),
});
export type ListItemListsQuery = z.infer<typeof listItemListsQuerySchema>;

export const itemListEntrySchema = z.object({
  name: z.string().trim().min(1, 'Required').max(200),
  sku: optionalText(60),
  description: optionalText(4000),
  price: money.default(0),
  taxRate: rate.default(0),
  isActive: z.coerce.boolean().default(true),
  /** The catalogue product this entry prices, if it is one. */
  productId: uuidSchema.nullish(),
});
export type ItemListEntryInput = z.infer<typeof itemListEntrySchema>;

export const updateItemListEntrySchema = toPatchSchema(itemListEntrySchema);
export type UpdateItemListEntryInput = z.infer<typeof updateItemListEntrySchema>;

/** Bulk entry: many entries in one request, all or nothing. */
export const bulkItemListEntriesSchema = z.object({
  entries: z.array(itemListEntrySchema).min(1).max(500),
});
export type BulkItemListEntriesInput = z.infer<typeof bulkItemListEntriesSchema>;

export interface ItemListSummary {
  id: string;
  name: string;
  year: number | null;
  effectiveFrom: string | null;
  effectiveTo: string | null;
  currency: string;
  status: ItemListStatus;
  notes: string | null;
  entryCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface ItemListEntryView {
  id: string;
  itemListId: string;
  productId: string | null;
  name: string;
  sku: string | null;
  description: string | null;
  price: string;
  taxRate: string;
  isActive: boolean;
  position: number;
}

/** A list entry as a line-item picker needs it: priced in the document's currency. */
export interface ItemListOption {
  id: string;
  itemListId: string;
  productId: string | null;
  name: string;
  sku: string | null;
  description: string | null;
  /** In the document's currency when one was asked for. */
  unitPrice: string;
  /** The entry's own price and the list's currency. */
  storedUnitPrice: string;
  storedCurrency: string;
  taxRate: string;
}

/** A reference to the list a document was priced from. */
export interface ItemListRef {
  id: string;
  name: string;
  year: number | null;
}
